import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { logout } from '../../utils/auth';

// ─── HuPath v2 Design Tokens ──────────────────────────────────────────────
const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  greenDark: '#5E9E3E',
  coral: '#FF8C42', coralDark: '#E76F51',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';
const SHADOW_SM = '0 2px 6px rgba(139,111,71,0.08)';

const TopNavBar = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const userName = localStorage.getItem('userFullName') || t('user');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const NAV_ITEMS = [
    { to: '/dashboard',      label: t('home'),              emoji: '🏠' },
    { to: '/full-mock-test', label: t('fullMockTestTitle'), emoji: '📄' },
    { to: '/vocab/words',    label: 'Sổ Từ',                 emoji: '📖' },
    { to: '/report',         label: t('report'),            emoji: '📊' },
    { to: '/settings',       label: t('settings'),          emoji: '⚙️' },
  ];

  return (
    <header
      style={{
        position: 'sticky', top: 0, zIndex: 50,
        width: '100%',
        background: '#FFF4E6',
        borderBottom: `4px solid ${HT.yellow}`,
        boxShadow: SHADOW_SM,
        fontFamily: FONT_BODY, color: HT.text,
      }}
    >
    <div style={{
      maxWidth: 1440, margin: '0 auto', padding: '10px 32px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
    }}>
      {/* Brand */}
      <Link to="/dashboard" style={{
        display: 'inline-flex', alignItems: 'center', gap: 2, lineHeight: 1,
        textDecoration: 'none',
        fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 20,
        color: HT.greenDark,
        padding: '3px 14px 3px 3px',
        background: HT.white,
        border: `2px solid ${HT.yellow}`,
        borderRadius: 999,
        boxShadow: `0 3px 0 ${HT.yellowShadow}`,
        flexShrink: 0,
      }}>
        <img src="/images/dashboard-v2/owl-mini.png" alt="HuPath" style={{ width: 38, height: 38, objectFit: 'contain', display: 'block', flexShrink: 0 }} />
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>HuPath</span>
      </Link>

      {/* Nav */}
      <nav className="hidden md:flex" style={{ gap: 6, alignItems: 'center' }}>
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
          return (
            <Link
              key={item.to}
              to={item.to}
              style={{
                padding: '7px 14px', borderRadius: 999,
                background: isActive ? HT.yellow : 'transparent',
                boxShadow: isActive ? `0 2px 0 ${HT.yellowShadow}` : 'none',
                fontSize: 13, fontWeight: 700,
                color: isActive ? HT.brownDark : HT.textSoft,
                textDecoration: 'none',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                letterSpacing: 0.2,
                transition: 'all 0.15s',
              }}
            >
              <span style={{ fontSize: 15 }}>{item.emoji}</span>
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* User chip with dropdown */}
      <div ref={userMenuRef} style={{ position: 'relative', zIndex: 10 }}>
        <div
          onClick={() => setShowUserMenu((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: HT.white, border: `2px solid ${HT.yellow}`,
            padding: '4px 12px 4px 4px', borderRadius: 999,
            cursor: 'pointer', userSelect: 'none',
          }}
        >
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            background: HT.coral,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, color: HT.white,
          }}>{(userName[0] || 'U').toUpperCase()}</div>
          <span
            className="hidden sm:inline"
            style={{
              fontSize: 13, fontWeight: 700, color: HT.text,
              maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >{userName}</span>
          <span style={{ color: HT.textMuted, fontSize: 10, flexShrink: 0 }}>▼</span>
        </div>
        {showUserMenu && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0,
            background: HT.white, borderRadius: 12,
            border: `2px solid ${HT.yellow}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: 160, overflow: 'hidden',
            fontFamily: FONT_BODY,
          }}>
            <button onClick={() => { setShowUserMenu(false); navigate('/settings'); }} style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              padding: '12px 16px', border: 'none', background: 'transparent',
              cursor: 'pointer', fontSize: 14, fontWeight: 600, color: HT.text,
              fontFamily: FONT_BODY,
            }}
              onMouseEnter={(e) => e.currentTarget.style.background = HT.yellowBg}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ fontSize: 16 }}>⚙️</span> {t('settings')}
            </button>
            <div style={{ height: 1, background: HT.border }} />
            <button onClick={() => { setShowUserMenu(false); logout(navigate); }} style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              padding: '12px 16px', border: 'none', background: 'transparent',
              cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#D64545',
              fontFamily: FONT_BODY,
            }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ fontSize: 16 }}>🚪</span> {t('logout') || 'Đăng Xuất'}
            </button>
          </div>
        )}
      </div>
    </div>
    </header>
  );
};

export default TopNavBar;
